/*
 * at-core.js — the musical half of /autotune: from a pitch track to the pitch
 * the voice should have. No audio here; at-psola.js does the singing.
 *
 *   const tr   = AutotuneCore.prepare(f0Hz, conf, hopSec);
 *   const key  = AutotuneCore.detectKey(tr);           // {tonic, mode, confidence, ...}
 *   const ref  = AutotuneCore.reference(tr);           // singer's own tuning, cents from A440
 *   const grid = AutotuneCore.grid(key.tonic, key.mode, refCents);
 *   const notes = AutotuneCore.segment(tr, grid);
 *   const out  = AutotuneCore.target(tr, notes, grid, params);   // corrected MIDI per frame
 *   const r    = AutotuneCore.ratios(tr, out);          // pitch ratio per frame, 1 where unvoiced
 *
 * Two ways to correct, because people want two different things:
 *  - natural: each NOTE moves as a whole — its centre lands on the target and
 *    everything the singer did inside it (vibrato, scoop, the slow drift) rides
 *    along. What a producer does by hand, and what one-slider tuners get wrong:
 *    a frame-by-frame pull flattens vibrato into the "robot" sound.
 *  - effect: every frame of a note is pulled onto the note's target at the
 *    retune speed — the hard-tune sound, on purpose. Same notes, same hand
 *    edits as the natural mode.
 * Notes are found on a smoothed pitch line with hysteresis, so a voice sitting
 * near the middle of two notes does not warble between them.
 *
 * MIDI floats throughout (69 = A4 440 Hz). Frames at a fixed hop.
 * ES2019, environment-neutral.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.AutotuneCore = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this), function () {
  'use strict';

  var NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  var MAJOR = [0, 2, 4, 5, 7, 9, 11];
  var MINOR = [0, 2, 3, 5, 7, 8, 10];            // natural minor

  function hzToMidi(hz) { return 69 + 12 * Math.log(hz / 440) / Math.LN2; }
  function midiToHz(m) { return 440 * Math.pow(2, (m - 69) / 12); }

  function median(arr) {
    if (!arr.length) return NaN;
    var s = Array.prototype.slice.call(arr).sort(function (a, b) { return a - b; });
    var h = s.length >> 1;
    return s.length & 1 ? s[h] : (s[h - 1] + s[h]) / 2;
  }

  // ------------------------------------------------------------------
  // Track preparation.
  // f0: Hz per frame (0 or NaN = unvoiced); conf: 0..1 per frame or null.
  // Returns {hop, n, midi (NaN where unvoiced), voiced (0/1), smooth}.
  // ------------------------------------------------------------------
  function prepare(f0, conf, hop, opts) {
    opts = opts || {};
    var n = f0.length;
    var minConf = opts.minConf != null ? opts.minConf : 0.5;
    var midi = new Float32Array(n);
    var voiced = new Uint8Array(n);
    var i;
    for (i = 0; i < n; i++) {
      var hz = f0[i];
      var ok = hz > 40 && hz < 2000 && (!conf || conf[i] >= minConf);
      voiced[i] = ok ? 1 : 0;
      midi[i] = ok ? hzToMidi(hz) : NaN;
    }
    var minRun = Math.max(1, Math.round((opts.minVoicedSec || 0.04) / hop));
    var maxGap = Math.max(0, Math.round((opts.maxGapSec || 0.03) / hop));
    // fill tiny unvoiced gaps inside a phrase (a consonant that the tracker
    // dropped for one or two frames): the correction must not jump there
    var runStart = -1, lastVoiced = -1;
    for (i = 0; i < n; i++) {
      if (voiced[i]) {
        if (lastVoiced >= 0 && i - lastVoiced - 1 > 0 && i - lastVoiced - 1 <= maxGap) {
          var a = midi[lastVoiced], b = midi[i];
          if (Math.abs(a - b) < 3) {
            for (var k = lastVoiced + 1; k < i; k++) {
              var u = (k - lastVoiced) / (i - lastVoiced);
              midi[k] = a + (b - a) * u;
              voiced[k] = 1;
            }
          }
        }
        lastVoiced = i;
      }
    }
    // octave slips: a stretch an octave away from what surrounds it
    fixOctaves(midi, voiced, Math.round(0.08 / hop));
    fixLongOctaves(midi, voiced, Math.round(0.6 / hop));
    // single-frame spikes
    median3(midi, voiced);
    // islands too short to be a note
    for (i = 0; i < n; i++) {
      if (voiced[i] && (i === 0 || !voiced[i - 1])) runStart = i;
      if (voiced[i] && (i === n - 1 || !voiced[i + 1])) {
        if (i - runStart + 1 < minRun) for (var j = runStart; j <= i; j++) { voiced[j] = 0; midi[j] = NaN; }
      }
    }
    var smooth = lowpass(midi, voiced, Math.max(1, Math.round((opts.smoothSec || 0.15) / hop)));
    return { hop: hop, n: n, midi: midi, voiced: voiced, smooth: smooth };
  }

  // A short run (up to `maxLen`) an octave away from the frames right next to
  // it: half (or twice) the period for a moment, not the singer.
  function fixOctaves(midi, voiced, maxLen) {
    var n = midi.length;
    for (var pass = 0; pass < 2; pass++) {
      var i = 0;
      while (i < n) {
        if (!voiced[i]) { i++; continue; }
        // a run of frames that stays together (no jump over 7 semitones inside)
        var s = i;
        while (i + 1 < n && voiced[i + 1] && Math.abs(midi[i + 1] - midi[i]) < 7) i++;
        var e = i;
        i++;
        if (e - s + 1 > maxLen) continue;
        var before = s > 0 && voiced[s - 1] ? midi[s - 1] : NaN;
        var after = e + 1 < n && voiced[e + 1] ? midi[e + 1] : NaN;
        if (isNaN(before) && isNaN(after)) continue;
        var ref = isNaN(before) ? after : (isNaN(after) ? before : (before + after) / 2);
        var mid = midi[(s + e) >> 1];
        var shift = octaveOff(mid - ref);
        if (shift) for (var k = s; k <= e; k++) midi[k] += shift;
      }
    }
  }
  function octaveOff(d) { return Math.abs(d - 12) < 2.5 ? -12 : (Math.abs(d + 12) < 2.5 ? 12 : 0); }

  // The same slip held longer. Measured on the 40 vocadito takes against
  // their F0 annotation (2026-10-23): 80 % of the gross errors were octaves,
  // in runs of 10–61 frames, which the 80 ms rule above does not reach. A
  // run of up to `maxLen` moves when the frames on BOTH sides of it agree
  // that it is an octave off AND at least one of the runs next to it is
  // longer than it — the longer side is the voice, the shorter one the slip.
  // Gross errors 2.10 % → 1.90 %, no take worse, notes within 10 cents after
  // tuning 86.7 → 86.8 %. Without the length test a correct run between two
  // short slips moved (voc24 +5.3 points, voc34 +3.7); asking both neighbours
  // to be longer fixed almost nothing (2.02 %).
  function fixLongOctaves(midi, voiced, maxLen) {
    var n = midi.length;
    var runs = [], i = 0;
    while (i < n) {
      if (!voiced[i]) { i++; continue; }
      var s = i;
      while (i + 1 < n && voiced[i + 1] && Math.abs(midi[i + 1] - midi[i]) < 7) i++;
      runs.push({ s: s, e: i });
      i++;
    }
    var shifts = [];
    for (var r = 1; r + 1 < runs.length; r++) {
      var run = runs[r], len = run.e - run.s + 1;
      if (len > maxLen) continue;
      var prev = runs[r - 1], next = runs[r + 1];
      if (prev.e !== run.s - 1 || next.s !== run.e + 1) continue;
      if (Math.max(prev.e - prev.s + 1, next.e - next.s + 1) < len) continue;
      var own = medianOf(midi, run.s, run.e);
      var sp = octaveOff(own - midi[run.s - 1]), sn = octaveOff(own - midi[run.e + 1]);
      if (sp && sp === sn) shifts.push([run.s, run.e, sp]);
    }
    // decided on the line as tracked, applied after: one correction must not
    // change the neighbours the next one is judged by
    for (var q = 0; q < shifts.length; q++) for (var k = shifts[q][0]; k <= shifts[q][1]; k++) midi[k] += shifts[q][2];
  }
  function medianOf(arr, a, b) {
    var v = [];
    for (var i = a; i <= b; i++) v.push(arr[i]);
    return median(v);
  }

  function median3(midi, voiced) {
    var n = midi.length, prev = midi.slice ? midi.slice() : Float32Array.from(midi);
    for (var i = 1; i < n - 1; i++) {
      if (!voiced[i] || !voiced[i - 1] || !voiced[i + 1]) continue;
      var a = prev[i - 1], b = prev[i], c = prev[i + 1];
      midi[i] = a > b ? (b > c ? b : (a > c ? c : a)) : (a > c ? a : (b > c ? c : b));
    }
  }

  // Centered moving average over voiced frames of one phrase (unvoiced frames
  // break it). With 150 ms it takes the vibrato (5–7 Hz) out and keeps the
  // melody and the slow drift.
  function lowpass(midi, voiced, win) {
    var n = midi.length, out = new Float32Array(n);
    var half = win >> 1;
    for (var i = 0; i < n; i++) {
      if (!voiced[i]) { out[i] = NaN; continue; }
      var sum = 0, cnt = 0;
      for (var k = i - half; k <= i + half; k++) {
        if (k < 0 || k >= n || !voiced[k]) continue;
        // stop at a phrase break
        var broke = false;
        var lo = Math.min(k, i), hi = Math.max(k, i);
        for (var q = lo; q <= hi; q++) if (!voiced[q]) { broke = true; break; }
        if (broke) continue;
        sum += midi[k]; cnt++;
      }
      out[i] = sum / cnt;
    }
    return out;
  }

  // ------------------------------------------------------------------
  // Tuning reference and key.
  // ------------------------------------------------------------------
  // Frames where the voice holds a pitch (the smoothed line barely moves).
  function steadyFrames(tr) {
    var out = [], sm = tr.smooth, v = tr.voiced;
    var lim = 0.06 * tr.hop / 0.01;        // semitones per frame
    for (var i = 1; i < tr.n - 1; i++) {
      if (!v[i] || !v[i - 1] || !v[i + 1]) continue;
      if (Math.abs(sm[i + 1] - sm[i - 1]) / 2 < lim) out.push(i);
    }
    return out;
  }

  // The singer's own A: circular mean of how far held pitches sit from the
  // equal-tempered grid, in cents (+ = sharp of 440). Confidence = length of
  // the mean vector (1 = everything agrees).
  function reference(tr) {
    var fr = steadyFrames(tr);
    var sx = 0, sy = 0;
    for (var i = 0; i < fr.length; i++) {
      var d = tr.smooth[fr[i]];
      var ang = 2 * Math.PI * (d - Math.round(d));
      sx += Math.cos(ang); sy += Math.sin(ang);
    }
    if (!fr.length) return { cents: 0, confidence: 0 };
    var c = Math.atan2(sy, sx) / (2 * Math.PI) * 100;
    return { cents: Math.round(c * 10) / 10, confidence: Math.sqrt(sx * sx + sy * sy) / fr.length, a4: 440 * Math.pow(2, c / 1200) };
  }

  function correlate(a, b) {
    var ma = 0, mb = 0, i;
    for (i = 0; i < 12; i++) { ma += a[i]; mb += b[i]; }
    ma /= 12; mb /= 12;
    var num = 0, da = 0, db = 0;
    for (i = 0; i < 12; i++) { num += (a[i] - ma) * (b[i] - mb); da += (a[i] - ma) * (a[i] - ma); db += (b[i] - mb) * (b[i] - mb); }
    return da && db ? num / Math.sqrt(da * db) : 0;
  }

  // Key from the sung notes: a log-duration pitch-class histogram against the
  // Aarden–Essen profiles in all 24 keys (Pearson). The same profiles
  // /audio-to-notes chose by measurement (2026-09-24): 97.1 % right key
  // signature on 757 POP909 melodies, against 80.8 % for Krumhansl–Kessler.
  // `notes` come from a CHROMATIC segmentation (the key is not known yet).
  var AE_MAJ = [17.7661, 0.145624, 14.9265, 0.160186, 19.8049, 11.3587, 0.291248, 22.062, 0.145624, 8.15494, 0.232998, 4.95122];
  var AE_MIN = [18.2648, 0.737619, 14.0499, 16.8599, 0.702494, 14.4362, 0.702494, 18.6161, 4.56621, 1.93186, 7.37619, 1.75623];
  function detectKey(tr, notes, refCents) {
    var hist = new Float64Array(12);
    var held = 0;
    for (var i = 0; i < notes.length; i++) {
      var nt = notes[i];
      var d = (nt.end - nt.start + 1) * tr.hop;
      var m = Math.round(nt.center - (refCents || 0) / 100);
      hist[((m % 12) + 12) % 12] += Math.log(1 + Math.max(0.05, d) * 4);
      held += d;
    }
    var ranking = [], rot = new Array(12);
    for (var t = 0; t < 12; t++) {
      for (var k = 0; k < 12; k++) rot[k] = hist[(t + k) % 12];
      ranking.push({ tonic: t, mode: 'major', r: correlate(rot, AE_MAJ) });
      ranking.push({ tonic: t, mode: 'minor', r: correlate(rot, AE_MIN) });
    }
    ranking.sort(function (a, b) { return b.r - a.r; });
    var best = ranking[0];
    // relative major/minor share their notes: that pair is one decision about
    // the scale, the tonic between them a second, softer one
    var rival = null;
    for (i = 1; i < ranking.length; i++) {
      var x = ranking[i];
      var relative = x.mode !== best.mode && ((best.mode === 'major' && x.tonic === (best.tonic + 9) % 12) || (best.mode === 'minor' && x.tonic === (best.tonic + 3) % 12));
      if (!relative) { rival = x; break; }
    }
    var margin = rival ? best.r - rival.r : best.r;
    var confidence = notes.length < 8 || held < 3 ? 'low' : (margin > 0.1 && best.r > 0.7 ? 'high' : (margin > 0.04 ? 'medium' : 'low'));
    return { tonic: best.tonic, mode: best.mode, r: best.r, margin: margin, confidence: confidence, notes: notes.length, heldSec: held, histogram: Array.prototype.slice.call(hist), ranking: ranking.slice(0, 6) };
  }

  // Which notes a sung pitch may land on.
  //   strict: 'soft'  — the nearest semitone, and the key only settles the
  //                     doubtful ones (more than 30 cents off): measured on
  //                     vocadito, a key guessed from a short amateur take is
  //                     wrong often enough that a hard scale pulls correctly
  //                     sung out-of-key notes a semitone away;
  //           'scale' — only notes of the key (the classic scale mode);
  //           'chromatic' — the nearest semitone, no key.
  // mode: 'major' | 'minor' | 'chromatic'; refCents: the singer's own A.
  function grid(tonic, mode, refCents, strict) {
    var pcs = new Array(12);
    var i;
    for (i = 0; i < 12; i++) pcs[i] = mode === 'chromatic';
    if (mode !== 'chromatic') {
      var steps = mode === 'minor' ? MINOR : MAJOR;
      for (i = 0; i < steps.length; i++) pcs[(tonic + steps[i]) % 12] = true;
    }
    return { pcs: pcs, offset: (refCents || 0) / 100, tonic: tonic, mode: mode, strict: mode === 'chromatic' ? 'chromatic' : (strict || 'soft') };
  }

  var DOUBT = 0.3;   // semitones from the nearest note where the key gets a say
  function inKey(g, c) { return g.pcs[((c % 12) + 12) % 12]; }

  // The note (on the singer's grid, returned with its offset) for pitch m.
  function nearest(g, m) {
    var x = m - g.offset;
    if (g.strict !== 'scale') {
      var c0 = Math.round(x);
      if (g.strict === 'soft' && !inKey(g, c0) && Math.abs(x - c0) > DOUBT) {
        var c1 = x > c0 ? c0 + 1 : c0 - 1;
        if (inKey(g, c1)) return c1 + g.offset;
      }
      return c0 + g.offset;
    }
    var base = Math.floor(x);
    var best = null, bestD = Infinity;
    for (var d = -2; d <= 3; d++) {
      var c = base + d;
      if (!inKey(g, c)) continue;
      var dist = Math.abs(x - c);
      if (dist < bestD) { bestD = dist; best = c; }
    }
    return best + g.offset;
  }

  // ------------------------------------------------------------------
  // Notes.
  // ------------------------------------------------------------------
  // opts: {hysteresis (semitones past the midpoint, 0.18), minNoteSec (0.06)}
  // Returns [{start, end, target, center, stable:[a,b]}] in frames.
  function segment(tr, g, opts) {
    opts = opts || {};
    var hyst = opts.hysteresis != null ? opts.hysteresis : 0.18;
    var minLen = Math.max(1, Math.round((opts.minNoteSec || 0.06) / tr.hop));
    var plateau = Math.max(2, Math.round((opts.plateauSec || 0.12) / tr.hop));
    var shiftLim = opts.plateauShift != null ? opts.plateauShift : 0.35;
    var n = tr.n, sm = tr.smooth, v = tr.voiced;
    var notes = [];
    var i = 0;
    while (i < n) {
      if (!v[i]) { i++; continue; }
      var s = i;
      while (i + 1 < n && v[i + 1]) i++;
      var e = i;
      i++;
      // one phrase [s, e]: quantise its smoothed line with hysteresis
      var cur = nearest(g, sm[s]), segStart = s;
      var cand = null, candStart = -1;
      var cuts = [];
      var sum = 0, cnt = 0, away = -1;
      var skipOn = Math.round(0.05 / tr.hop);
      for (var k = s; k <= e; k++) {
        var q = nearest(g, sm[k]);
        if (q === cur) {
          cand = null;
          // same note, but the voice moved to another level and stayed there:
          // a new stretch with its own correction (one shift for both would
          // leave the second level out of tune)
          if (k - segStart >= skipOn) {
            if (cnt >= plateau && Math.abs(sm[k] - sum / cnt) > shiftLim) {
              if (away < 0) away = k;
              if (k - away + 1 >= plateau) {
                cuts.push({ at: away, note: cur });
                segStart = away; sum = 0; cnt = 0; away = -1;
                for (var z = segStart; z <= k; z++) { sum += sm[z]; cnt++; }
                continue;
              }
            } else {
              away = -1;
              sum += sm[k]; cnt++;
            }
          }
          continue;
        }
        away = -1;
        // switch only when clearly past the midpoint toward q
        var mid = (cur + q) / 2;
        var past = q > cur ? sm[k] - mid : mid - sm[k];
        if (past < hyst) { cand = null; continue; }
        if (cand !== q) { cand = q; candStart = k; }
        if (k - candStart + 1 >= minLen) {
          // the change happened where the line crossed the midpoint
          var at = candStart;
          while (at > segStart + 1 && ((q > cur && sm[at - 1] > mid) || (q < cur && sm[at - 1] < mid))) at--;
          cuts.push({ at: at, note: cur });
          segStart = at;
          cur = q;
          cand = null;
          sum = 0; cnt = 0; away = -1;
        }
      }
      cuts.push({ at: e + 1, note: cur });
      var from = s;
      for (var c = 0; c < cuts.length; c++) {
        notes.push(makeNote(tr, from, cuts[c].at - 1, cuts[c].note));
        from = cuts[c].at;
      }
    }
    // a sliver between two notes is the glide, not a note: give it to the
    // neighbour it is closer to
    var out = [];
    for (var j = 0; j < notes.length; j++) {
      var nt = notes[j];
      var len = nt.end - nt.start + 1;
      if (len < minLen && out.length && out[out.length - 1].end === nt.start - 1) {
        var prev = out[out.length - 1];
        prev.end = nt.end;
        out[out.length - 1] = makeNote(tr, prev.start, prev.end, prev.target);
        continue;
      }
      out.push(nt);
    }
    return out;
  }

  function makeNote(tr, start, end, target) {
    // the held part: skip the scoop at the start and the fall at the end
    var len = end - start + 1;
    var a = start + Math.floor(len * 0.2), b = end - Math.floor(len * 0.15);
    if (b < a) { a = start; b = end; }
    var vals = [];
    for (var i = a; i <= b; i++) if (tr.voiced[i]) vals.push(tr.midi[i]);
    var center = vals.length ? centreOf(vals, tr.hop) : target;
    return { start: start, end: end, target: target, center: center, stable: [a, b] };
  }

  // Where a held note SOUNDS. With a vibrato: the mean pitch over whole
  // vibrato cycles — the ear hears a vibrato tone at about its mean (Shonle &
  // Horan 1980; d'Alessandro & Castellengo 1994), and a median, or a mean over
  // 2.3 cycles, leans toward whichever half-cycle is left over (on a
  // synthetic ±25-cent vibrato that put notes up to 9.5 cents off after
  // tuning). Without one: the median, which a leftover scoop or a glitch does
  // not move. Measured on the 40 vocadito takes (2026-10-01), notes within 10
  // cents of their target after tuning, judged by a median / by a cycle mean:
  // the median alone 82.6 / 79.4 %, the cycle mean alone 65.8 / 81.7 %, this
  // split 80.9 / 80.9 % — and 0.3 cents on a synthetic ±25–50-cent vibrato. A vibrato = at least one whole cycle of 3.5–10 Hz, found by crossings of
  // the note's mean with hysteresis, so noise does not cut them.
  function centreOf(vals, hop) {
    var n = vals.length, i, mean = 0, sd = 0;
    if (!n) return NaN;
    for (i = 0; i < n; i++) mean += vals[i];
    mean /= n;
    if (n < 8) return median(vals);
    for (i = 0; i < n; i++) sd += (vals[i] - mean) * (vals[i] - mean);
    sd = Math.sqrt(sd / n);
    var h = Math.max(0.02, 0.3 * sd);                // semitones
    var ups = [], low = false;
    for (i = 0; i < n; i++) {
      var d = vals[i] - mean;
      if (d < -h) low = true;
      else if (low && d > 0) { ups.push(i); low = false; }
    }
    var fr = 1 / (hop || 0.01);                      // frames per second
    var cycles = 0;
    for (i = 1; i < ups.length; i++) {
      var per = (ups[i] - ups[i - 1]) / fr;
      if (per >= 0.1 && per <= 0.29) cycles++;
    }
    if (cycles < 1 || sd < 0.06) return median(vals);
    var s = 0, c = 0;
    for (i = ups[0]; i < ups[ups.length - 1]; i++) { s += vals[i]; c++; }
    return s / c;
  }

  // ------------------------------------------------------------------
  // Corrected pitch.
  // params: {mode: 'natural'|'effect', strength 0..1, speedMs, vibrato 0..1
  //          (natural: how much of the vibrato stays), drift 0..1 (natural:
  //          how much of the slow wander inside a note is flattened),
  //          overrides: {noteIndex: targetMidi | null (= leave as sung)}}
  // Returns Float32Array of MIDI per frame (NaN where unvoiced).
  // ------------------------------------------------------------------
  function target(tr, notes, g, params) {
    params = params || {};
    var n = tr.n, hop = tr.hop;
    var strength = params.strength != null ? params.strength : 1;
    var speed = Math.max(0, params.speedMs != null ? params.speedMs : 40) / 1000;
    var out = new Float32Array(n);
    var i;
    for (i = 0; i < n; i++) out[i] = tr.voiced[i] ? tr.midi[i] : NaN;
    var ov = params.overrides || {};
    // Notes are named on the singer's own grid (g.offset); where they LAND is
    // a separate choice: A = 440 (default — the voice then fits any backing
    // track), the voice's own A (the least movement), or the backing track's
    // own A (`beatCents` from A440: a beat a quarter-tone off is common, and a
    // voice tuned to 440 over it is in tune and still sounds wrong).
    var land = landOf(g, params);
    // per note: where it goes (a hand edit wins; null = leave as sung)
    var tgt = new Float32Array(notes.length);
    var skip = new Uint8Array(notes.length);
    for (var k = 0; k < notes.length; k++) {
      var t = notes[k].target + land;
      if (Object.prototype.hasOwnProperty.call(ov, k)) {
        if (ov[k] === null) { skip[k] = 1; t = notes[k].center; } else t = ov[k] + land;
      }
      tgt[k] = t;
    }
    if (params.mode === 'effect') return effect(tr, notes, tgt, skip, strength, speed, out);

    var vib = params.vibrato != null ? params.vibrato : 1;
    var drift = params.drift != null ? params.drift : 0.3;
    // per note: the shift that moves its centre onto the target
    var shift = new Float32Array(notes.length);
    for (k = 0; k < notes.length; k++) shift[k] = skip[k] ? 0 : (tgt[k] - notes[k].center) * strength;
    var ramp = Math.max(1, Math.round(speed / hop));
    for (k = 0; k < notes.length; k++) {
      var nt = notes[k];
      var prevJoined = k > 0 && notes[k - 1].end === nt.start - 1;
      for (i = nt.start; i <= nt.end; i++) {
        if (!tr.voiced[i]) continue;
        var m = tr.midi[i], sm = tr.smooth[i];
        // what the singer did inside the note, split into drift and vibrato
        var dr = sm - nt.center, vb = m - sm;
        var within = skip[k] ? 0 : (-drift * dr + (vib - 1) * vb) * strength;
        var sh = shift[k];
        // into the note: from the previous note's shift (glide) or from zero
        // (an attack after silence) over the retune time
        var d = i - nt.start;
        if (d < ramp) {
          var from = prevJoined ? shift[k - 1] : 0;
          var u = (d + 1) / (ramp + 1);
          u = u * u * (3 - 2 * u);
          sh = from + (sh - from) * u;
          within *= u;
        }
        out[i] = m + sh + within;
      }
    }
    return out;
  }

  // Where the notes of grid g land, in semitones from the grid they are
  // named on (see target()).
  function landOf(g, params) {
    params = params || {};
    if (params.tuneTo === 'voice') return 0;
    if (params.tuneTo === 'beat' && isFinite(params.beatCents)) return params.beatCents / 100 - g.offset;
    return -g.offset;
  }

  // Hard tune: every frame of a note straight onto its target — the same
  // notes and the same hand edits as the natural mode, so a note moved on the
  // graph moves here too — with a one-pole glide of `speed` from one target to
  // the next. Vibrato and scoops are gone: that is the sound, on purpose.
  // (Before 2026-10-01 this mode picked its own note per frame: it disagreed
  // with the notes on the graph and ignored the edits.)
  function effect(tr, notes, tgt, skip, strength, speed, out) {
    var hop = tr.hop;
    var a = speed > 0 ? 1 - Math.exp(-hop / speed) : 1;
    var y = NaN, lastEnd = -2;
    for (var k = 0; k < notes.length; k++) {
      var nt = notes[k];
      if (nt.start !== lastEnd + 1) y = NaN;        // a new phrase starts on its note
      lastEnd = nt.end;
      for (var i = nt.start; i <= nt.end; i++) {
        if (!tr.voiced[i]) { y = NaN; continue; }
        var m = tr.midi[i];
        if (skip[k]) { y = m; continue; }           // as sung; the next note glides from here
        y = isNaN(y) ? tgt[k] : y + (tgt[k] - y) * a;
        out[i] = m + (y - m) * strength;
      }
    }
    return out;
  }

  // A second voice over the tuned one: each note moved by `steps` degrees of
  // the key (2 = a third, 4 = a fifth, −3 = a fourth below, 7 = an octave);
  // `semis` instead moves every note by a fixed interval (12 = octave).
  // Diatonic, so a third is major or minor wherever the key says it is — a
  // fixed +4 puts half the notes of a minor song out of key. Without a key
  // (chromatic grid) there is no scale to count in: a third becomes a fifth,
  // the one interval that agrees with any note. Notes left as sung (null in
  // overrides) get no harmony. Returns MIDI per frame, NaN where silent.
  function harmony(tr, notes, out, g, spec, overrides) {
    var n = tr.n, h = new Float32Array(n), i, k;
    for (i = 0; i < n; i++) h[i] = NaN;
    var ov = overrides || {};
    var scale = [];
    for (i = 0; i < 12; i++) if (g.pcs[i]) scale.push(i);
    var diatonic = g.mode !== 'chromatic' && scale.length === 7 && spec.steps != null;
    for (k = 0; k < notes.length; k++) {
      if (Object.prototype.hasOwnProperty.call(ov, k) && ov[k] === null) continue;
      var nt = notes[k];
      // the note as it now sounds (its frames' median on the tuned line)
      var vals = [];
      for (i = nt.start; i <= nt.end; i++) if (!isNaN(out[i])) vals.push(out[i]);
      if (!vals.length) continue;
      var delta;
      if (spec.semis != null) delta = spec.semis;
      else if (!diatonic) delta = spec.steps < 0 ? -5 : 7;
      else {
        var m = Math.round(median(vals) - g.offset);
        var pc = ((m % 12) + 12) % 12;
        // a note outside the key counts from the degree under it
        var idx = 0;
        for (var d = 0; d < 7; d++) if (scale[d] <= pc) idx = d;
        var to = idx + spec.steps;
        var oct = Math.floor(to / 7), deg = ((to % 7) + 7) % 7;
        delta = scale[deg] + 12 * oct - pc;
      }
      for (i = nt.start; i <= nt.end; i++) if (!isNaN(out[i])) h[i] = out[i] + delta;
    }
    return h;
  }

  // Every note of the take on one pitch: the classic monotone robot, and a
  // quick start for a spoken phrase. `to` is MIDI on the singer's grid; by
  // default the most common pitch of the take. Returns the overrides map.
  function monotone(notes, to) {
    var ov = {};
    if (to == null) {
      var cnt = {}, best = null;
      for (var k = 0; k < notes.length; k++) {
        var t = notes[k].target, len = notes[k].end - notes[k].start + 1;
        cnt[t] = (cnt[t] || 0) + len;
        if (best === null || cnt[t] > cnt[best]) best = t;
      }
      to = best;
    }
    for (var j = 0; j < notes.length; j++) if (Math.abs(notes[j].target - to) > 1e-6) ov[j] = to;
    return ov;
  }

  // Pitch ratio per frame (output / input frequency); 1 where unvoiced.
  function ratios(tr, corrected) {
    var r = new Float32Array(tr.n);
    for (var i = 0; i < tr.n; i++) {
      r[i] = tr.voiced[i] && !isNaN(corrected[i]) ? Math.pow(2, (corrected[i] - tr.midi[i]) / 12) : 1;
    }
    return r;
  }

  // How many notes sit within `tol` cents of their target: the held part's
  // median, measured on `curve` (the original or the corrected pitch).
  function score(tr, notes, curve, tol, land) {
    tol = tol || 10;
    land = land || 0;
    var hit = 0, total = 0, sum = 0;
    for (var k = 0; k < notes.length; k++) {
      var nt = notes[k];
      if (nt.end - nt.start + 1 < 8) continue;
      var vals = [];
      for (var i = nt.stable[0]; i <= nt.stable[1]; i++) if (tr.voiced[i] && !isNaN(curve[i])) vals.push(curve[i]);
      if (vals.length < 4) continue;
      var c = Math.abs(centreOf(vals, tr.hop) - (nt.target + land)) * 100;
      total++; sum += c;
      if (c <= tol) hit++;
    }
    return { notes: total, within: total ? hit / total : 0, meanCents: total ? sum / total : 0 };
  }

  function keyName(tonic, mode) { return NAMES[tonic] + (mode === 'minor' ? 'm' : ''); }

  return {
    NAMES: NAMES,
    hzToMidi: hzToMidi,
    midiToHz: midiToHz,
    prepare: prepare,
    reference: reference,
    detectKey: detectKey,
    grid: grid,
    nearest: nearest,
    segment: segment,
    target: target,
    ratios: ratios,
    harmony: harmony,
    monotone: monotone,
    landOf: landOf,
    score: score,
    keyName: keyName,
    centreOf: centreOf
  };
});
